/*  PERCUP — PER-287 · A–Z Brands directory
 *
 *  Every brand card is already in the DOM (rendered by
 *  sections/brands-directory.liquid), so this file only ever hides, shows and
 *  reorders what is there. No fetching, no pagination, no re-render — which is
 *  why filtering stays instant at ~100 brands and the page still works with
 *  JS off, just unfiltered.
 *
 *  State lives in the URL so a filtered view is shareable and survives a back
 *  button. Every param is `b`-prefixed (?bcat=&bcountry=&bq=&bsort=&bletter=)
 *  because a bare `q` is Shopify's own search parameter — the theme's search
 *  picks it up and navigates the shopper off this page mid-type.
 */
(function () {
  'use strict';

  function init(root) {
    if (root.dataset.bdReady === '1') return;
    root.dataset.bdReady = '1';

    var grid = root.querySelector('[data-bd-grid]');
    if (!grid) return;

    var cards = Array.prototype.slice.call(grid.querySelectorAll('[data-brand]'));
    var catBtns = Array.prototype.slice.call(root.querySelectorAll('[data-bd-cat]'));
    var markers = Array.prototype.slice.call(root.querySelectorAll('[data-bd-marker]'));
    var alphaBtns = Array.prototype.slice.call(root.querySelectorAll('[data-bd-letter]'));
    var searchEl = root.querySelector('[data-bd-search]');
    var searchClear = root.querySelector('[data-bd-search-clear]');
    var sortEl = root.querySelector('[data-bd-sort]');
    var chipsEl = root.querySelector('[data-bd-chips]');
    var emptyEl = root.querySelector('[data-bd-empty]');
    var titleEl = root.querySelector('[data-bd-results-title]');
    var subEl = root.querySelector('[data-bd-count-sub]');
    var heroReset = root.querySelector('.bd-hero-reset');
    var total = cards.length;
    // PER-335 — the brand tally is hidden from the public until 200+ brands.
    // Liquid omits the count elements entirely; this keeps the runtime updates
    // from putting them back and keeps the wording consistent.
    var showCounts = root.dataset.showCounts === '1';

    // Cache the sort keys once — reading them back off the DOM in a comparator
    // is what makes naive versions of this janky.
    var meta = cards.map(function (el, i) {
      return {
        el: el,
        i: i,
        name: el.dataset.name || '',
        sortName: el.dataset.sortName || '',
        // Searches the full collection title, so a brand whose title carries an
        // SEO tail is still findable by words the card does not show.
        search: (el.dataset.search || el.dataset.name || '').toLowerCase(),
        cats: (el.dataset.cats || '').split(/\s+/).filter(Boolean),
        country: el.dataset.country || '',
        count: parseInt(el.dataset.count, 10) || 0,
        added: parseInt(el.dataset.added, 10) || 0,
        isNew: el.dataset.new === '1',
        letter: el.dataset.letter || '#'
      };
    });

    var labels = {};
    catBtns.forEach(function (b) {
      labels[b.dataset.bdCat] = (b.textContent || '').replace(/\s*\d+\s*$/, '').trim();
    });
    var countryLabels = {};
    markers.forEach(function (m) {
      countryLabels[m.dataset.country] =
        m.dataset.countryLabel || (m.getAttribute('aria-label') || '').split('—')[0].trim();
    });

    var allLabel = labels.all || 'All brands';

    var state = {
      cat: 'all',
      country: '',
      q: '',
      letter: 'all',
      sort: root.dataset.defaultSort || 'az'
    };

    // ── url ────────────────────────────────────────────────────────────────
    function readUrl() {
      var p = new URLSearchParams(window.location.search);
      if (p.get('bcat')) state.cat = p.get('bcat');
      if (p.get('bcountry')) state.country = p.get('bcountry');
      if (p.get('bq')) state.q = p.get('bq');
      if (p.get('bletter')) state.letter = p.get('bletter');
      if (p.get('bsort')) state.sort = p.get('bsort');
      // A category id from another theme-editor session is meaningless here.
      if (state.cat !== 'all' && !labels[state.cat]) state.cat = 'all';
      if (state.country && !countryLabels[state.country]) state.country = '';
    }

    function writeUrl() {
      var p = new URLSearchParams(window.location.search);
      var set = function (k, v, dflt) {
        if (v && v !== dflt) p.set(k, v);
        else p.delete(k);
      };
      set('bcat', state.cat, 'all');
      set('bcountry', state.country, '');
      set('bq', state.q, '');
      set('bletter', state.letter, 'all');
      set('bsort', state.sort, root.dataset.defaultSort || 'az');
      var qs = p.toString();
      var url = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
      window.history.replaceState(null, '', url);
    }

    // ── matching ───────────────────────────────────────────────────────────
    function matchesExceptCountry(m) {
      if (state.cat !== 'all' && m.cats.indexOf(state.cat) === -1) return false;
      if (state.letter !== 'all') {
        if (state.letter === '#') {
          if (/^[A-Z]$/.test(m.letter)) return false;
        } else if (m.letter !== state.letter) return false;
      }
      if (state.q && m.search.indexOf(state.q.toLowerCase()) === -1) return false;
      return true;
    }

    function matches(m) {
      if (!matchesExceptCountry(m)) return false;
      if (state.country && m.country !== state.country) return false;
      return true;
    }

    // ── sorting ────────────────────────────────────────────────────────────
    var collator =
      typeof Intl !== 'undefined' && Intl.Collator
        ? new Intl.Collator(undefined, { sensitivity: 'base', numeric: true })
        : null;

    function cmpName(a, b) {
      return collator
        ? collator.compare(a.name, b.name)
        : a.sortName < b.sortName
        ? -1
        : a.sortName > b.sortName
        ? 1
        : 0;
    }

    var sorters = {
      az: cmpName,
      za: function (a, b) {
        return cmpName(b, a);
      },
      count: function (a, b) {
        return b.count - a.count || cmpName(a, b);
      },
      new: function (a, b) {
        return b.isNew - a.isNew || b.added - a.added || cmpName(a, b);
      }
    };

    var sortedBy = null;
    function applySort() {
      if (sortedBy === state.sort) return;
      sortedBy = state.sort;
      var order = meta.slice().sort(sorters[state.sort] || sorters.az);
      var frag = document.createDocumentFragment();
      order.forEach(function (m) {
        frag.appendChild(m.el);
      });
      grid.appendChild(frag);
    }

    // ── chips ──────────────────────────────────────────────────────────────
    function addChip(label, onRemove) {
      var chip = document.createElement('span');
      chip.className = 'bd-fchip';
      chip.appendChild(document.createTextNode(label));
      var x = document.createElement('button');
      x.type = 'button';
      x.innerHTML = '&#10005;';
      x.setAttribute('aria-label', 'Remove filter: ' + label);
      x.addEventListener('click', onRemove);
      chip.appendChild(x);
      chipsEl.appendChild(chip);
    }

    function isFiltered() {
      return state.cat !== 'all' || !!state.country || !!state.q || state.letter !== 'all';
    }

    // ── render ─────────────────────────────────────────────────────────────
    function render() {
      applySort();

      var shown = 0;
      meta.forEach(function (m) {
        var ok = matches(m);
        if (m.el.hidden === ok) m.el.hidden = !ok;
        if (ok) shown++;
      });

      // Marker counts ignore the country filter, so you can always see how many
      // brands the country you are about to click actually holds.
      markers.forEach(function (mk) {
        var slug = mk.dataset.country;
        var n = 0;
        for (var i = 0; i < meta.length; i++) {
          if (meta[i].country === slug && matchesExceptCountry(meta[i])) n++;
        }
        var cnt = mk.querySelector('[data-bd-marker-count]');
        if (cnt && showCounts) cnt.textContent = n;
        mk.hidden = n === 0 && state.country !== slug;
        var on = state.country === slug;
        mk.setAttribute('aria-pressed', on ? 'true' : 'false');
        mk.setAttribute(
          'aria-label',
          showCounts
            ? countryLabels[slug] + ' — ' + n + ' brand' + (n === 1 ? '' : 's')
            : 'Filter by ' + countryLabels[slug]
        );
      });

      // Category counts respect everything except the category itself.
      catBtns.forEach(function (b) {
        var id = b.dataset.bdCat;
        var n = 0;
        for (var i = 0; i < meta.length; i++) {
          var m = meta[i];
          if (state.country && m.country !== state.country) continue;
          if (state.letter !== 'all') {
            if (state.letter === '#') {
              if (/^[A-Z]$/.test(m.letter)) continue;
            } else if (m.letter !== state.letter) continue;
          }
          if (state.q && m.search.indexOf(state.q.toLowerCase()) === -1) continue;
          if (id === 'all' || m.cats.indexOf(id) !== -1) n++;
        }
        var badge = b.querySelector('.bd-n');
        if (badge && showCounts) badge.textContent = n;
        // Never remove a category — grey it out, same as the collection filters.
        if (id !== 'all') b.disabled = n === 0 && state.cat !== id;
        var on = state.cat === id;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      alphaBtns.forEach(function (b) {
        var l = b.dataset.bdLetter;
        var on = state.letter === l;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (l !== 'all') {
          var has = meta.some(function (m) {
            if (state.cat !== 'all' && m.cats.indexOf(state.cat) === -1) return false;
            if (state.country && m.country !== state.country) return false;
            if (state.q && m.search.indexOf(state.q.toLowerCase()) === -1) return false;
            return l === '#' ? !/^[A-Z]$/.test(m.letter) : m.letter === l;
          });
          b.disabled = !has && !on;
        }
      });

      // Heading + count
      var parts = [];
      if (state.country) parts.push(countryLabels[state.country]);
      if (state.cat !== 'all') parts.push(labels[state.cat]);
      if (state.letter !== 'all') parts.push(state.letter === '#' ? '0–9' : state.letter);
      if (titleEl) titleEl.textContent = parts.length ? parts.join(' · ') : allLabel;
      if (subEl) {
        subEl.textContent = showCounts
          ? 'Showing ' + shown + ' of ' + total + ' brand' + (total === 1 ? '' : 's')
          : 'Showing ' + shown + ' brand' + (shown === 1 ? '' : 's');
      }

      // Removable chips
      if (chipsEl) {
        chipsEl.innerHTML = '';
        if (state.cat !== 'all') {
          addChip(labels[state.cat], function () {
            state.cat = 'all';
            commit();
          });
        }
        if (state.country) {
          addChip(countryLabels[state.country], function () {
            state.country = '';
            commit();
          });
        }
        if (state.letter !== 'all') {
          addChip(state.letter === '#' ? '0–9' : state.letter, function () {
            state.letter = 'all';
            commit();
          });
        }
        if (state.q) {
          addChip('“' + state.q + '”', function () {
            state.q = '';
            if (searchEl) searchEl.value = '';
            commit();
          });
        }
        if (chipsEl.children.length) {
          var clear = document.createElement('button');
          clear.type = 'button';
          clear.className = 'bd-clearall';
          clear.textContent = 'Clear all';
          clear.addEventListener('click', resetAll);
          chipsEl.appendChild(clear);
        }
      }

      if (emptyEl) emptyEl.hidden = shown !== 0;
      if (searchClear) searchClear.hidden = !state.q;
      if (heroReset) heroReset.hidden = !isFiltered();
      grid.setAttribute('aria-busy', 'false');
    }

    function commit() {
      render();
      writeUrl();
    }

    function resetAll() {
      state.cat = 'all';
      state.country = '';
      state.q = '';
      state.letter = 'all';
      if (searchEl) searchEl.value = '';
      commit();
    }

    // ── events ─────────────────────────────────────────────────────────────
    catBtns.forEach(function (b) {
      b.addEventListener('click', function () {
        state.cat = state.cat === b.dataset.bdCat ? 'all' : b.dataset.bdCat;
        commit();
      });
    });

    markers.forEach(function (mk) {
      mk.addEventListener('click', function () {
        state.country = state.country === mk.dataset.country ? '' : mk.dataset.country;
        commit();
      });
    });

    alphaBtns.forEach(function (b) {
      b.addEventListener('click', function () {
        state.letter = b.dataset.bdLetter;
        commit();
      });
    });

    if (searchEl) {
      var t;
      searchEl.addEventListener('input', function () {
        clearTimeout(t);
        t = setTimeout(function () {
          state.q = searchEl.value.trim();
          commit();
        }, 90);
      });
      searchEl.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && searchEl.value) {
          e.preventDefault();
          searchEl.value = '';
          state.q = '';
          commit();
        }
      });
      // A search box inside a page that has no <form> should not submit anything.
      searchEl.form && searchEl.form.addEventListener('submit', function (e) {
        e.preventDefault();
      });
    }

    if (searchClear) {
      searchClear.addEventListener('click', function () {
        if (searchEl) searchEl.value = '';
        state.q = '';
        commit();
        if (searchEl) searchEl.focus();
      });
    }

    if (sortEl) {
      sortEl.addEventListener('change', function () {
        state.sort = sortEl.value;
        commit();
      });
    }

    root.querySelectorAll('[data-bd-reset]').forEach(function (b) {
      b.addEventListener('click', resetAll);
    });

    root.querySelectorAll('[data-bd-popular]').forEach(function (b) {
      b.addEventListener('click', function () {
        var term = b.dataset.bdPopular;
        // Tapping the same pill twice clears it, so it reads as a toggle.
        state.q = state.q === term ? '' : term;
        if (searchEl) searchEl.value = state.q;
        commit();
      });
    });

    // ── map zoom ───────────────────────────────────────────────────────────
    // Zoom belongs to the map and nowhere else. Pinching the page was zooming
    // the whole site — header, cards and all — because there was nothing here
    // to catch it. Scaling the stage (artwork + markers together) keeps every
    // pin locked to its country at any zoom level.
    (function () {
      var mapEl = root.querySelector('[data-bd-map]');
      var stage = root.querySelector('[data-bd-map-stage]');
      var zoomBar = root.querySelector('[data-bd-map-zoom]');
      if (!mapEl || !stage || !zoomBar) return;

      var MIN = 1;
      var MAX = 4;
      var scale = 1;
      var panX = 0;
      var panY = 0;

      var btnIn = zoomBar.querySelector('[data-bd-zoom="in"]');
      var btnOut = zoomBar.querySelector('[data-bd-zoom="out"]');
      var btnReset = zoomBar.querySelector('[data-bd-zoom="reset"]');

      // Keep the panned view from sliding the map off its own frame.
      function clampPan() {
        var r = mapEl.getBoundingClientRect();
        var maxX = (r.width * (scale - 1)) / 2;
        var maxY = (r.height * (scale - 1)) / 2;
        panX = Math.max(-maxX, Math.min(maxX, panX));
        panY = Math.max(-maxY, Math.min(maxY, panY));
      }

      // ── pin de-overlap ───────────────────────────────────────────────────
      // On a phone the map is ~370px wide and the six western-European pins sit
      // within about 17px of each other, so they overlap and you cannot reliably
      // tap the one you meant. This pushes overlapping pins just far enough
      // apart to be separately tappable, capped so a pin never wanders off its
      // country. It re-runs on zoom because real separation grows with scale —
      // by ~2x the nudge has shrunk to nothing on its own.
      var pins = markers.map(function (el) {
        return {
          el: el,
          px: parseFloat(el.style.left) || 0, // % of map width
          py: parseFloat(el.style.top) || 0,
          ox: 0,
          oy: 0
        };
      });

      function deOverlap() {
        var r = mapEl.getBoundingClientRect();
        if (!r.width) return;
        var dot = mapEl.querySelector('.bd-dot');
        // offsetWidth is the layout size and ignores transforms. getBoundingClientRect
        // would sample the dot mid-transition and give a different answer every run,
        // which made the spacing jump around after a zoom round trip.
        var size = dot ? dot.offsetWidth : 12;
        // dots are counter-scaled, so their screen size no longer grows with zoom
        var minGap = size + 4;
        var maxNudge = size * 1.4;

        var pts = pins.map(function (p) {
          return { x: (p.px / 100) * r.width * scale, y: (p.py / 100) * r.height * scale, bx: 0, by: 0 };
        });

        for (var pass = 0; pass < 40; pass++) {
          var moved = false;
          for (var i = 0; i < pts.length; i++) {
            for (var j = i + 1; j < pts.length; j++) {
              var a = pts[i], b = pts[j];
              var dx = b.x + b.bx - (a.x + a.bx);
              var dy = b.y + b.by - (a.y + a.by);
              var d = Math.hypot(dx, dy);
              if (d >= minGap) continue;
              moved = true;
              // identical coordinates would divide by zero — nudge deterministically
              if (d < 0.01) { dx = (j % 2 ? 1 : -1); dy = 0; d = 1; }
              var push = (minGap - d) / 2;
              var ux = (dx / d) * push, uy = (dy / d) * push;
              a.bx -= ux; a.by -= uy;
              b.bx += ux; b.by += uy;
            }
          }
          if (!moved) break;
        }

        pts.forEach(function (pt, i) {
          var mag = Math.hypot(pt.bx, pt.by);
          if (mag > maxNudge) {
            pt.bx = (pt.bx / mag) * maxNudge;
            pt.by = (pt.by / mag) * maxNudge;
          }
          // the offset lands inside the stage's scaled space, so divide it back out
          pins[i].el.style.setProperty('--bd-ox', pt.bx / scale + 'px');
          pins[i].el.style.setProperty('--bd-oy', pt.by / scale + 'px');
        });
      }

      function apply() {
        clampPan();
        stage.style.transform =
          'translate(' + panX + 'px,' + panY + 'px) scale(' + scale + ')';
        // Keep pins a constant size on screen so zooming genuinely spreads them.
        stage.style.setProperty('--bd-mscale', 1 / scale);
        mapEl.classList.toggle('is-zoomed', scale > 1);
        btnIn.disabled = scale >= MAX - 0.001;
        btnOut.disabled = scale <= MIN + 0.001;
        btnReset.hidden = scale <= MIN + 0.001;
        deOverlap();
      }

      var resizeIdle;
      window.addEventListener('resize', function () {
        clearTimeout(resizeIdle);
        resizeIdle = setTimeout(apply, 120);
      });

      // Zoom about a point so the country under the cursor stays put.
      function zoomTo(next, originX, originY) {
        next = Math.max(MIN, Math.min(MAX, next));
        if (next === scale) return;
        var r = mapEl.getBoundingClientRect();
        var cx = originX === undefined ? r.width / 2 : originX;
        var cy = originY === undefined ? r.height / 2 : originY;
        var dx = cx - r.width / 2;
        var dy = cy - r.height / 2;
        var ratio = next / scale;
        panX = (panX - dx) * ratio + dx;
        panY = (panY - dy) * ratio + dy;
        scale = next;
        if (scale === MIN) {
          panX = 0;
          panY = 0;
        }
        apply();
      }

      btnIn.addEventListener('click', function () {
        zoomTo(scale * 1.6);
      });
      btnOut.addEventListener('click', function () {
        zoomTo(scale / 1.6);
      });
      btnReset.addEventListener('click', function () {
        scale = MIN;
        panX = 0;
        panY = 0;
        apply();
      });

      mapEl.addEventListener(
        'wheel',
        function (e) {
          if (!e.ctrlKey && !e.metaKey) return; // plain scroll stays page scroll
          e.preventDefault();
          var r = mapEl.getBoundingClientRect();
          mapEl.classList.add('is-interacting');
          zoomTo(scale * (e.deltaY < 0 ? 1.12 : 1 / 1.12), e.clientX - r.left, e.clientY - r.top);
          clearTimeout(wheelIdle);
          wheelIdle = setTimeout(function () {
            mapEl.classList.remove('is-interacting');
          }, 180);
        },
        { passive: false }
      );
      var wheelIdle;

      mapEl.addEventListener('dblclick', function (e) {
        if (e.target.closest('[data-bd-marker]') || e.target.closest('[data-bd-map-zoom]')) return;
        var r = mapEl.getBoundingClientRect();
        zoomTo(scale >= MAX - 0.001 ? MIN : scale * 1.8, e.clientX - r.left, e.clientY - r.top);
      });

      // ── drag to pan, pointer events so mouse and touch share one path ──
      var dragging = false;
      var moved = false;
      var startX = 0;
      var startY = 0;
      var originPanX = 0;
      var originPanY = 0;
      var pointers = new Map();
      var pinchStart = 0;
      var pinchScale = 1;

      function dist() {
        var pts = Array.from(pointers.values());
        return Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      }

      mapEl.addEventListener('pointerdown', function (e) {
        if (e.target.closest('[data-bd-map-zoom]')) return;
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointers.size === 2) {
          pinchStart = dist();
          pinchScale = scale;
          dragging = false;
          mapEl.classList.add('is-interacting');
          return;
        }
        if (scale <= MIN + 0.001) return; // nothing to pan at 1x
        dragging = true;
        moved = false;
        startX = e.clientX;
        startY = e.clientY;
        originPanX = panX;
        originPanY = panY;
        mapEl.classList.add('is-interacting');
        mapEl.setPointerCapture(e.pointerId);
      });

      mapEl.addEventListener('pointermove', function (e) {
        if (pointers.has(e.pointerId)) pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

        // Two fingers inside the map pinch the MAP, never the page.
        if (pointers.size === 2 && pinchStart > 0) {
          e.preventDefault();
          var r = mapEl.getBoundingClientRect();
          var pts = Array.from(pointers.values());
          zoomTo(
            pinchScale * (dist() / pinchStart),
            (pts[0].x + pts[1].x) / 2 - r.left,
            (pts[0].y + pts[1].y) / 2 - r.top
          );
          return;
        }

        if (!dragging) return;
        e.preventDefault();
        var mx = e.clientX - startX;
        var my = e.clientY - startY;
        if (Math.abs(mx) > 3 || Math.abs(my) > 3) moved = true;
        panX = originPanX + mx;
        panY = originPanY + my;
        apply();
      });

      function endPointer(e) {
        pointers.delete(e.pointerId);
        if (pointers.size < 2) pinchStart = 0;
        dragging = false;
        if (pointers.size === 0) mapEl.classList.remove('is-interacting');
      }
      mapEl.addEventListener('pointerup', endPointer);
      mapEl.addEventListener('pointercancel', endPointer);

      // A drag that ends on a pin should pan, not filter.
      mapEl.addEventListener(
        'click',
        function (e) {
          if (moved) {
            e.stopPropagation();
            e.preventDefault();
            moved = false;
          }
        },
        true
      );

      apply();
    })();

    // ── request form ───────────────────────────────────────────────────────
    // Shopify emails every contact[...] field, so the form works without this.
    // We only compose contact[body] so the notification reads as a sentence.
    var reqForm = root.querySelector('.bd-rform-form');
    var reqBody = root.querySelector('[data-bd-req-body]');
    if (reqForm && reqBody) {
      reqForm.addEventListener('submit', function () {
        var val = function (k) {
          var el = reqForm.querySelector('[data-bd-req="' + k + '"]');
          return el ? (el.value || '').trim() : '';
        };
        var brand = val('brand');
        var lines = ['Brand requested: ' + (brand || '—')];
        if (val('link')) lines.push('Website / Instagram: ' + val('link'));
        if (val('cat')) lines.push('Category: ' + val('cat'));
        lines.push('Submitted from the A–Z Brands page.');
        reqBody.value = lines.join('\n');
      });
    }

    // ── boot ───────────────────────────────────────────────────────────────
    readUrl();
    if (searchEl && state.q) searchEl.value = state.q;
    if (sortEl) sortEl.value = state.sort;
    render();
  }

  function boot() {
    document.querySelectorAll('[data-brands-directory]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor re-renders the section on every setting change.
  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-brands-directory]').forEach(init);
  });
})();
